import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Container,
  AlertTitle,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  Payment as PaymentIcon,
  AccountCircle as AccountCircleIcon,
  Info as InfoIcon,
  ShoppingBag as ShoppingBagIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import Layout from '../../components/Layout/Layout';
import styles from './CustomerDetailsPage.module.css';
import BackButton from '../../components/BackButton/BackButton';
import Breadcrumb, { BreadcrumbItem } from '../../components/Breadcrumb/Breadcrumb';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { getCustomerById } from '../../services/customer-service';
import { getOrdersByCustomerId } from '../../services/order-service';
import { Customer, Address, Order } from '../../types';
import { formatCnpj, formatPhone, formatZipCode } from '../../utils/formatters';

// Interface definitions
interface PaymentMethod {
  id: string;
  method: string;
  bank: string;
  due_date: string;
  is_default: boolean;
}

interface Note {
  id: string;
  title: string;
  content: string;
  date: string;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  model: string;
  image: string;
}

// Since we're using additional properties not in the base type,
// we'll extend the Customer type for our component
interface CustomerExtended extends Customer {
  contact_name?: string;
  contact_role?: string;
}

// Mock data for demonstration
const mockProducts: Product[] = [
  {
    id: '1',
    sku: 'DT-R1HAL-001',
    name: 'Notebook Gamer Acer Nitro V',
    model: 'AN515-51-50GL',
    image: '/images/image-19.png',
  },
  {
    id: '2',
    sku: 'DT-R1HAL-002',
    name: 'Notebook Acer Aspire 5',
    model: 'A515-54-59W2',
    image: '/images/image-19.png',
  },
  {
    id: '3',
    sku: 'DT-R1HAL-003',
    name: 'Monitor Acer Nitro',
    model: 'XZ2-3440X1440',
    image: '/images/image-19.png',
  },
  {
    id: '4',
    sku: 'DT-R1HAL-004',
    name: 'Desktop Acer Predator',
    model: 'PO3-600',
    image: '/images/image-19.png',
  },
];

const mockPaymentMethods: PaymentMethod[] = [
  {
    id: '1',
    method: 'Boleto 30/60/90',
    bank: 'Itaú - Agência 1234 - Conta 56789-0',
    due_date: 'Todo dia 10 de cada mês',
    is_default: true,
  },
  {
    id: '2',
    method: 'Cartão de Crédito',
    bank: 'Itaú - Agência 1234 - Conta 56789-0',
    due_date: 'Todo dia 15 de cada mês',
    is_default: false,
  },
];

const mockNotes: Note[] = [
  {
    id: '1',
    title: 'Observação X',
    content: 'Centro de Distribuição InfoTech. Informação XPTO.',
    date: '18/01/2025',
  },
];

const CustomerDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const { setBreadcrumbs } = useBreadcrumb();

  // State for customer data and orders
  const [customer, setCustomer] = useState<CustomerExtended | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch customer data and orders
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch customer details
        const customerResponse = await getCustomerById(id);
        if (!customerResponse.success) {
          throw new Error(customerResponse.message || 'Failed to fetch customer details');
        }

        // Find the primary contact if available
        const primaryContact =
          customerResponse.data.contacts?.find(contact => contact.is_primary) || null;

        const customerData: CustomerExtended = {
          ...customerResponse.data,
          contact_name: primaryContact?.name || '',
          contact_role: primaryContact?.role || '',
          // Use primary contact email/phone if available, otherwise fall back to customer data
          email: primaryContact?.email || customerResponse.data.email,
          phone: primaryContact?.phone || customerResponse.data.phone,
        };

        setCustomer(customerData);
        setAddresses(customerResponse.data.addresses || []);

        // Fetch customer orders
        const ordersResponse = await getOrdersByCustomerId(id);
        if (!ordersResponse.success) {
          throw new Error(ordersResponse.message || 'Failed to fetch customer orders');
        }

        setOrders(ordersResponse.data);
      } catch (err) {
        console.error('Error fetching customer data:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  // For mock data that isn't part of the API yet
  const products = mockProducts;
  const notes = mockNotes;
  const paymentMethods = mockPaymentMethods;

  useEffect(() => {
    // Set breadcrumbs when component mounts or customer data changes
    if (customer) {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
        { label: 'Painel de Clientes', path: '/customers' },
        { label: customer.company_name },
      ]);
    }

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> }]);
    };
  }, [customer, setBreadcrumbs]);

  const handleNoteSubmit = () => {
    // In a real app, you would dispatch an action to add the note
    console.log('Note submitted:', note);
    setNote('');
  };

  const handleEditCustomer = () => {
    console.log(id);
    navigate(`/customers/${id}/edit`);
  };

  const handleAddOrder = () => {
    navigate('/orders/new', { state: { customer_id: id } });
  };

  const handleAddQuote = () => {
    navigate('/quotes/new', { state: { customer_id: id } });
  };

  const handleAddAddress = () => {
    // Logic to add a new address
    navigate(`/customers/${id}/edit`, { state: { activeTab: 1 } });
  };

  const handleEditAddress = (addressId: string) => {
    // Logic to edit an address
    navigate(`/customers/${id}/edit`, { state: { activeTab: 1 } });
  };

  const handleEditPayment = () => {
    // Logic to edit payment methods
    navigate(`/customers/${id}/edit`, { state: { activeTab: 3 } });
  };

  const handleViewAllOrders = () => {
    navigate('/orders', { state: { filter: { customer_id: id } } });
  };

  const handleOrderDetails = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const renderStatusChip = (status: string) => {
    // Map backend status values to UI status values if needed
    const normalizedStatus = normalizeOrderStatus(status);

    switch (normalizedStatus) {
      case 'processing':
        return (
          <Chip
            size="small"
            sx={{
              bgcolor: '#f3f3f3',
              color: '#505050',
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '12px',
            }}
            label="Processando"
          />
        );
      case 'shipping':
        return (
          <Chip
            size="small"
            sx={{
              bgcolor: '#d6f1ff',
              color: '#212d75',
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '12px',
            }}
            label="Em transporte"
          />
        );
      case 'delivered':
        return (
          <Chip
            size="small"
            sx={{
              bgcolor: '#edf7ed',
              color: '#1e4620',
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '12px',
            }}
            label="Enviado"
          />
        );
      default:
        return (
          <Chip
            size="small"
            sx={{
              bgcolor: '#f3f3f3',
              color: '#505050',
              fontSize: '0.75rem',
              height: '24px',
              borderRadius: '12px',
            }}
            label="Desconhecido"
          />
        );
    }
  };

  // Helper function to normalize order status from backend to frontend format
  const normalizeOrderStatus = (status: string): string => {
    // Handle case-insensitive comparison and different naming conventions
    const statusLower = status.toLowerCase();

    if (statusLower.includes('process') || statusLower === 'pending') {
      return 'processing';
    } else if (
      statusLower.includes('ship') ||
      statusLower === 'sent' ||
      statusLower === 'transit'
    ) {
      return 'shipping';
    } else if (statusLower.includes('deliver') || statusLower === 'completed') {
      return 'delivered';
    }

    return statusLower;
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Carregando detalhes do cliente...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography>Erro ao carregar detalhes do cliente: {error}</Typography>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
        <Typography>Cliente não encontrado</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Back Button */}
      <BackButton sx={{ mb: 2 }} />

      {/* Customer Info Card */}
      <Paper sx={{ mb: 3, borderRadius: '8px', overflow: 'hidden', bgcolor: '#353535' }}>
        <Box sx={{ p: '16px 20px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src="/icons/store.svg"
                alt="Opportunity icon"
                style={{
                  width: 24,
                  height: 24,
                  marginRight: '8px',
                }}
              />
              <Box>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  {customer.company_name}
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<EditIcon />}
              onClick={handleEditCustomer}
              sx={{ color: '#fff', textTransform: 'none', fontSize: '0.875rem' }}
            >
              Atualizar dados
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="#fff">
                CNPJ: {customer.cnpj ? formatCnpj(customer.cnpj) : ''} | Setor: {customer.sector}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'self-end' }}>
            <Box>
              <Typography variant="body2" color="#fff" sx={{ mb: 1 }}>
                Contato Comercial:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  icon={<AccountCircleIcon fontSize="small" />}
                  label={customer.contact_name}
                  size="small"
                  sx={{ bgcolor: '#f3f3f3', color: '#505050', fontSize: '0.75rem' }}
                />
                <Chip
                  icon={<EmailIcon fontSize="small" />}
                  label={customer.email}
                  size="small"
                  sx={{ bgcolor: '#f3f3f3', color: '#505050', fontSize: '0.75rem' }}
                />
                <Chip
                  icon={<PhoneIcon fontSize="small" />}
                  label={customer.phone ? formatPhone(customer.phone) : ''}
                  size="small"
                  sx={{ bgcolor: '#f3f3f3', color: '#505050', fontSize: '0.75rem' }}
                />
                <Chip
                  icon={<BusinessIcon fontSize="small" />}
                  label={customer.contact_role}
                  size="small"
                  sx={{ bgcolor: '#f3f3f3', color: '#505050', fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              {/* <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddQuote}
                sx={{
                  bgcolor: '#83b81a',
                  '&:hover': { bgcolor: '#70a310' },
                  textTransform: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                }}
              >
                Criar novo pedido
              </Button> */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddQuote}
                sx={{
                  color: '#83b81a',
                  borderColor: '#83b81a',
                  '&:hover': { borderColor: '#70a310', bgcolor: 'rgba(131, 184, 26, 0.1)' },
                  textTransform: 'none',
                  borderRadius: '4px',
                }}
              >
                Criar nova cotação
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Two Column Layout */}
      <Grid container spacing={3}>
        {/* Favorite Products */}
        {/* 
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 1, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f3f3f3',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  mr: 2,
                }}
              >
                <FavoriteIcon sx={{ color: '#83b81a' }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#2b3445', fontWeight: 600 }}>
                Produtos favoritos
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Lista de produtos que esse cliente costuma comprar.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {products.map(product => (
                <Box
                  key={product.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: '#f3f3f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <img src={product.image} alt={product.name} width={60} height={60} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      SKU: {product.sku}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#2b3445', fontWeight: 500 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Modelo: {product.model}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              sx={{
                bgcolor: '#2b3445',
                '&:hover': { bgcolor: '#1a202c' },
                textTransform: 'none',
                mt: 3,
                borderRadius: '4px',
              }}
            >
              Cotar produtos
            </Button>
          </Paper>
        </Grid>
        */}

        {/* Intelligent Suggestions */}
        {/*
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 1, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f3f3f3',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  mr: 2,
                }}
              >
                <InfoIcon sx={{ color: '#83b81a' }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#2b3445', fontWeight: 600 }}>
                Sugestões Inteligentes
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Baseado no histórico de compras, aqui estão sugestões para melhorar suas vendas
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert
                severity="success"
                sx={{
                  borderLeft: '4px solid #83b81a',
                  bgcolor: '#fafff6',
                  borderRadius: '4px',
                  '.MuiAlert-icon': { color: '#83b81a' },
                }}
              >
                <AlertTitle sx={{ color: '#2b3445', fontWeight: 500 }}>Oportunidade!</AlertTitle>
                <Typography variant="body2" color="textSecondary">
                  Este cliente costuma comprar Monitores Acer a cada 3 meses. Sugerimos entrar em
                  contato para nova cotação.
                </Typography>
              </Alert>

              <Alert
                severity="warning"
                sx={{
                  borderLeft: '4px solid #ef6c00',
                  bgcolor: '#fff4e5',
                  borderRadius: '4px',
                  '.MuiAlert-icon': { color: '#ef6c00' },
                }}
              >
                <AlertTitle sx={{ color: '#2b3445', fontWeight: 500 }}>Atenção</AlertTitle>
                <Typography variant="body2" color="textSecondary">
                  O último pedido deste cliente foi há mais de 60 dias. Recomendamos um contato para
                  entender suas próximas demandas e manter um bom relacionamento.
                </Typography>
              </Alert>

              <Alert
                severity="success"
                sx={{
                  borderLeft: '4px solid #83b81a',
                  bgcolor: '#fafff6',
                  borderRadius: '4px',
                  '.MuiAlert-icon': { color: '#83b81a' },
                }}
              >
                <AlertTitle sx={{ color: '#2b3445', fontWeight: 500 }}>Oportunidade</AlertTitle>
                <Typography variant="body2" color="textSecondary">
                  Os pedidos deste cliente geralmente são feitos no início do mês. Que tal oferecer
                  uma condição especial antecipada para incentivá-lo a comprar mais?
                </Typography>
              </Alert>

              <Alert
                severity="success"
                sx={{
                  borderLeft: '4px solid #83b81a',
                  bgcolor: '#fafff6',
                  borderRadius: '4px',
                  '.MuiAlert-icon': { color: '#83b81a' },
                }}
              >
                <AlertTitle sx={{ color: '#2b3445', fontWeight: 500 }}>Oportunidade</AlertTitle>
                <Typography variant="body2" color="textSecondary">
                  Este cliente tem histórico de compra de Notebooks, mas nunca comprou acessórios.
                  Sugira um combo com garantia estendida e mochila para aumentar o ticket médio.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>
        */}
      </Grid>

      {/* Wrap Registered Addresses and Payment Methods in a Grid container */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* Registered Addresses - 1/3 width */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: 1, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <img
                src="/icons/distance.svg"
                alt="Opportunity icon"
                style={{
                  width: 24,
                  height: 24,
                  marginRight: '8px',
                }}
              />
              <Typography variant="h6" sx={{ color: '#2b3445', fontWeight: 600 }}>
                Endereços Cadastrados
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {addresses.map(address => (
                <Grid item xs={12} key={address.id}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: '4px', borderColor: '#d9d9d9' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#83b81a', fontWeight: 500 }}>
                        {address.is_default ? 'Endereço de Faturamento' : 'Endereço de Entrega'}{' '}
                        {address.is_default && (
                          <span style={{ color: '#505050', fontWeight: 'normal' }}>
                            (Principal)
                          </span>
                        )}
                      </Typography>
                      <IconButton size="small" onClick={() => handleEditAddress(address.id)}>
                        <EditIcon fontSize="small" sx={{ color: '#505050' }} />
                      </IconButton>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#505050', fontWeight: 500 }}>
                        {address.is_default ? 'Endereço de Faturamento' : 'Endereço de Entrega'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {address.street} - {address.city} - {address.state},{' '}
                        {address.zip_code ? formatZipCode(address.zip_code) : ''}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAddress}
                sx={{
                  bgcolor: 'var(--acer-dark)',
                  '&:hover': { bgcolor: '#1a202c' },
                  textTransform: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                }}
              >
                Adicionar endereço
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Orders - 2/3 width (moved here to replace Payment Methods) */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 1, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <img
                src="/icons/ultimos-pedidos.svg"
                alt="Opportunity icon"
                style={{
                  width: 24,
                  height: 24,
                  marginRight: '8px',
                }}
              />
              <Typography variant="h6" sx={{ color: '#2b3445', fontWeight: 600 }}>
                Últimos pedidos
              </Typography>
            </Box>

            <TableContainer sx={{ mb: 2, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: '#83b81a',
                        color: 'white',
                        fontWeight: 500,
                        borderTopLeftRadius: '4px',
                        borderBottomLeftRadius: '4px',
                        '&:first-of-type': {
                          paddingLeft: 2,
                        },
                      }}
                    >
                      Código do pedido
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#83b81a', color: 'white', fontWeight: 500 }}>
                      Data
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#83b81a', color: 'white', fontWeight: 500 }}>
                      Valor total
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#83b81a', color: 'white', fontWeight: 500 }}>
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: '#83b81a',
                        color: 'white',
                        fontWeight: 500,
                        borderTopRightRadius: '4px',
                        borderBottomRightRadius: '4px',
                      }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length > 0 ? (
                    orders.map(order => (
                      <TableRow key={order.id}>
                        <TableCell sx={{ borderBottom: '1px solid #d9d9d9', py: 1.5 }}>
                          {order.quote?.code || order.code || order.id}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #d9d9d9', py: 1.5 }}>
                          {new Date(order.created_at || '').toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #d9d9d9', py: 1.5 }}>
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(
                            order.order_items
                              ? order.order_items.reduce(
                                  (total: number, item: { quantity: number; unit_price: number }) =>
                                    total + item.quantity * item.unit_price,
                                  0
                                )
                              : order.total || 0
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #d9d9d9', py: 1.5 }}>
                          {renderStatusChip(order.status)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #d9d9d9', py: 1.5 }}>
                          {/* Removed details link */}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhum pedido encontrado para este cliente.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleViewAllOrders}
                sx={{
                  bgcolor: 'var(--acer-dark)',
                  '&:hover': { bgcolor: '#1a202c' },
                  textTransform: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                }}
              >
                Ver todos pedidos
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: 1, p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img
                src="/icons/add-notes.svg"
                alt="Opportunity icon"
                style={{
                  width: 24,
                  height: 24,
                  marginRight: '8px',
                }}
              />
              <Typography variant="h6" sx={{ color: '#2b3445', fontWeight: 600 }}>
                Adicionar Nota Interna
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Comentários e observações sobre histórico de atendimento.
            </Typography>

            <TextField
              multiline
              minRows={4}
              maxRows={6}
              placeholder="Observação:"
              fullWidth
              variant="outlined"
              value={note}
              onChange={e => setNote(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                  fontSize: '0.875rem',
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleNoteSubmit}
                sx={{
                  bgcolor: 'var(--acer-dark)',
                  '&:hover': { bgcolor: '#1a202c' },
                  textTransform: 'none',
                  borderRadius: '4px',
                  color: '#fff',
                }}
              >
                Salvar nota
              </Button>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#2b3445', mb: 2 }}>
                Histórico
              </Typography>

              {notes.map(note => (
                <Paper
                  key={note.id}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: '4px', borderColor: '#d9d9d9', mb: 2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#83b81a', fontWeight: 500 }}>
                      {note.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        {note.date}
                      </Typography>
                      <IconButton size="small">
                        <DeleteIcon fontSize="small" sx={{ color: '#505050' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {note.content}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid> */}
      </Grid>
    </>
  );
};

export default CustomerDetailsPage;
